import React, { useEffect, useRef, useState } from 'react';
import cytoscape from 'cytoscape';
import coseBilkent from 'cytoscape-cose-bilkent';
import './DatabaseGraph.css';
import { API_URL, isLocalhost } from '../config';
import dagre from 'cytoscape-dagre';
import klay from 'cytoscape-klay';
import euler from 'cytoscape-euler';
import fcose from 'cytoscape-fcose';
import cola from 'cytoscape-cola';
import cise from 'cytoscape-cise';
import elk from 'cytoscape-elk';

// Register the cose-bilkent layout
cytoscape.use(coseBilkent);

// Import additional layout extensions
cytoscape.use(dagre);
cytoscape.use(klay);
cytoscape.use(euler);
cytoscape.use(fcose);
cytoscape.use(cola);
cytoscape.use(cise);
cytoscape.use(elk);

// Move the config to a separate file or environment variables
const APP_CONFIG = {
    version: {
        erp: "11.3.100"
    },
    zoom: {
        min: 0.2,
        max: 3.0
    },
    colors: {
        base: '#4a90e2',
        selected: '#ff0000',
        highlightedEdge: '#00cc00',
        connected: [
            '#00ff00',    // depth 1
            '#33ff33',    // depth 2
            '#66ff66',    // depth 3
            '#99ff99',    // depth 4
            '#ccffcc'     // depth 5
        ]
    }
};

const DatabaseGraph = () => {
    const cyRef = useRef(null);
    const containerRef = useRef(null);
    const [selectedNodes, setSelectedNodes] = useState(new Set());
    const [paths, setPaths] = useState([]);
    const [graphHeight, setGraphHeight] = useState('calc(100vh - 300px)');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [relationships, setRelationships] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentLayout, setCurrentLayout] = useState('fcose');
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [nodeSeparation, setNodeSeparation] = useState(80); // Default value for node separation
    
    // Helper function to show toast messages
    const showToast = (message, type = 'success') => {
        const toast = document.getElementById('toast');
        if (!toast) return;
        
        toast.textContent = message;
        toast.className = `toast ${type}`;
        toast.style.display = 'block';
        
        setTimeout(() => {
            toast.style.display = 'none';
        }, 3000);
    };
    
    // Toggle sidebar collapse state
    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
        
        // Update the width of the graph container immediately
        const cyContainer = document.getElementById('cy');
        if (cyContainer) {
            if (isSidebarCollapsed) {
                // Sidebar is being expanded, reduce graph width
                cyContainer.style.width = 'calc(100% - 250px)';
            } else {
                // Sidebar is being collapsed, expand graph
                cyContainer.style.width = '100%';
            }
        }
        
        // Trigger a resize event to ensure the graph adjusts
        setTimeout(() => {
            if (cyRef.current) {
                // Force a resize of the window to trigger Cytoscape resize
                window.dispatchEvent(new Event('resize'));
                
                cyRef.current.resize();
                cyRef.current.fit();
                
                // Ensure edges are visible after sidebar toggle
                cyRef.current.edges().style({
                    'display': 'element',
                    'visibility': 'visible',
                    'opacity': 1
                });
            }
        }, 300); // Wait for the transition to complete
    };
    
    // Toggle relationship group expansion
    const toggleRelationshipGroup = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };
    
    // Function to build hierarchical relationships
    const buildRelationships = (node, depth = 1, maxDepth = 5, visited = new Set(), path = [], parentNode = null) => {
        if (depth > maxDepth) return [];
        if (!node) return [];
        
        const cy = cyRef.current;
        if (!cy) return [];
        
        const nodeId = node.id();
        
        // Skip if we've already visited this node in the current path
        if (visited.has(nodeId)) return [];
        
        // Add this node to the visited set for this path
        const newVisited = new Set(visited);
        newVisited.add(nodeId);
        
        // Get all connected edges
        const connectedEdges = node.connectedEdges();
        
        // Initialize relationships array
        let relationships = [];
        
        // Process each edge
        connectedEdges.forEach(edge => {
            const source = edge.source();
            const target = edge.target();
            
            // Skip self-loops
            if (source.id() === target.id()) return;
            
            // Determine if this node is the source or target
            const isSource = source.id() === nodeId;
            const otherNode = isSource ? target : source;
            
            // Skip if the other node is in the visited set
            if (visited.has(otherNode.id())) return;
            
            // Get field relationships
            const sourceId = source.id();
            const targetId = target.id();
            const relationshipKey = `${sourceId}-${targetId}`;
            const reverseRelationshipKey = `${targetId}-${sourceId}`;
            
            const fieldRelationships = window.fieldRelationships && 
                (window.fieldRelationships[relationshipKey] || 
                 window.fieldRelationships[reverseRelationshipKey]);
            
            // Add this relationship
            relationships.push({
                source: isSource ? node : otherNode,
                target: isSource ? otherNode : node,
                edge: edge,
                depth: depth,
                parentNode: parentNode || node,
                path: [...path, nodeId],
                fieldRelationships: fieldRelationships || []
            });
            
            // Recursively get relationships for the other node
            if (depth < maxDepth) {
                const childRelationships = buildRelationships(
                    otherNode,
                    depth + 1,
                    maxDepth,
                    newVisited,
                    [...path, nodeId],
                    node
                );
                
                // Add child relationships to the result
                relationships = relationships.concat(childRelationships);
            }
        });
        
        return relationships;
    };
    
    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (cyRef.current) {
                cyRef.current.resize();
                cyRef.current.fit();
                
                // Ensure edges remain visible after resize
                setTimeout(() => {
                    if (cyRef.current) {
                        cyRef.current.edges().style({
                            'display': 'element',
                            'visibility': 'visible',
                            'opacity': 1
                        });
                    }
                }, 100);
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    
    // Add resize functionality for the sidebar (flyout panel)
    useEffect(() => {
        const initResizeHandle = () => {
            const sidebar = document.querySelector('.sidebar');
            
            if (!sidebar) return;
            
            // Create a resize handle if it doesn't exist
            let resizeHandle = document.querySelector('.sidebar-resize-handle');
            if (!resizeHandle) {
                resizeHandle = document.createElement('div');
                resizeHandle.className = 'sidebar-resize-handle';
                resizeHandle.title = 'Drag to resize sidebar width';
                sidebar.appendChild(resizeHandle);
            }
            
            let startX, startWidth;
            
            const startResize = (e) => {
                e.preventDefault();
                startX = e.clientX;
                startWidth = parseInt(document.defaultView.getComputedStyle(sidebar).width, 10);
                document.addEventListener('mousemove', resize);
                document.addEventListener('mouseup', stopResize);
                
                // Add a class to indicate resizing is in progress
                document.body.classList.add('sidebar-resizing');
                sidebar.style.transition = 'none'; // Disable transitions during resize
            };
            
            const resize = (e) => {
                // Calculate new width (resize from right to left)
                const newWidth = startWidth - (e.clientX - startX);
                // Ensure minimum width of 200px and maximum of 500px
                const clampedWidth = Math.min(Math.max(200, newWidth), 500);
                sidebar.style.width = `${clampedWidth}px`;
                
                // Update the graph container width
                const cyContainer = document.getElementById('cy');
                if (cyContainer) {
                    cyContainer.style.width = `calc(100% - ${clampedWidth}px)`;
                }
            };
            
            const stopResize = () => {
                document.removeEventListener('mousemove', resize);
                document.removeEventListener('mouseup', stopResize);
                document.body.classList.remove('sidebar-resizing');
                sidebar.style.transition = ''; // Restore transitions
                
                // Force a redraw of the graph
                setTimeout(() => {
                    if (cyRef.current) {
                        cyRef.current.resize();
                        cyRef.current.fit();
                        
                        // Ensure edges are visible after resize
                        cyRef.current.edges().style({
                            'display': 'element',
                            'visibility': 'visible',
                            'opacity': 1
                        });
                    }
                    
                    // Dispatch a resize event
                    window.dispatchEvent(new Event('resize'));
                }, 100);
            };
            
            resizeHandle.addEventListener('mousedown', startResize);
            
            return () => {
                resizeHandle.removeEventListener('mousedown', startResize);
                document.removeEventListener('mousemove', resize);
                document.removeEventListener('mouseup', stopResize);
            };
        };
        
        // Initialize resize handle
        const timeoutId = setTimeout(initResizeHandle, 100);
        return () => clearTimeout(timeoutId);
    }, []);
    
    useEffect(() => {
        // Initialize Cytoscape after a small delay to ensure DOM is ready
        const initCytoscape = () => {
            const container = document.getElementById('cy');
            
            // Make sure the container exists before initializing Cytoscape
            if (!container) {
                console.error('Cytoscape container element not found');
                return;
            }
            
            // Set container to full width minus sidebar
            container.style.width = 'calc(100% - 250px)';
            
            // Apply dark mode to container if enabled
            if (isDarkMode) {
                container.style.backgroundColor = '#222';
                document.body.classList.add('dark-mode');
            } else {
                container.style.backgroundColor = '#f8f9fa';
                document.body.classList.remove('dark-mode');
            }
            
        const cy = cytoscape({
                container: container,
            style: [
                    {
                        selector: 'node',
                        style: {
                            'background-color': APP_CONFIG.colors.base,
                            'label': 'data(label)',
                            'text-valign': 'center',
                            'text-halign': 'center',
                            'color': '#fff',
                            'font-size': '12px',
                            'text-outline-width': 1,
                            'text-outline-color': '#4a90e2',
                            'width': 'mapData(connections, 0, 20, 30, 80)',
                            'height': 'mapData(connections, 0, 20, 30, 80)',
                            'border-width': 2,
                            'border-color': '#fff'
                        }
                    },
                    {
                        selector: 'edge',
                        style: {
                            'width': 2,
                            'line-color': '#ccc',
                            'target-arrow-color': '#ccc',
                            'target-arrow-shape': 'triangle',
                            'curve-style': 'bezier',
                            'label': 'data(label)',
                            'font-size': '10px',
                            'text-rotation': 'autorotate',
                            'text-margin-y': -10,
                            'text-background-color': '#fff',
                            'text-background-opacity': 0.7,
                            'text-background-padding': 2,
                            'visibility': 'visible',
                            'opacity': 1,
                            'display': 'element',
                            'z-index': 999,
                            'overlay-opacity': 0,
                            'text-events': 'yes',
                            'min-zoomed-font-size': 8,
                            'edge-text-rotation': 'autorotate',
                            'arrow-scale': 1.5,
                            'text-outline-width': 2,
                            'text-outline-color': '#fff',
                            'text-outline-opacity': 0.8,
                            'line-style': 'solid'
                        }
                    },
                    {
                        selector: 'node:selected',
                        style: {
                            'background-color': APP_CONFIG.colors.selected,
                            'border-width': 3,
                            'border-color': '#000'
                        }
                    },
                    {
                        selector: 'edge:selected',
                        style: {
                            'width': 4,
                            'line-color': APP_CONFIG.colors.selected,
                            'target-arrow-color': APP_CONFIG.colors.selected,
                            'source-arrow-color': APP_CONFIG.colors.selected,
                            'text-background-color': '#fff',
                            'text-background-opacity': 1
                        }
                    },
                    {
                        selector: '.highlighted',
                        style: {
                            'background-color': APP_CONFIG.colors.connected[0],
                            'line-color': '#00cc00',
                            'target-arrow-color': '#00cc00',
                            'transition-property': 'background-color, line-color, target-arrow-color',
                            'transition-duration': '0.5s'
                        }
                    }
            ],
            layout: {
                name: 'cose-bilkent',
                    fit: true,
                    padding: 10, // Reduce padding to use more space
                    nodeDimensionsIncludeLabels: true,
                    randomize: true,
                    nodeRepulsion: 8000,
                    idealEdgeLength: 80,
                    edgeElasticity: 0.45,
                    nestingFactor: 0.1,
                    gravity: 0.25,
                    numIter: 2500,
                    tile: true,
                    animate: 'end',
                    animationDuration: 500,
                    refresh: 20,
                    packComponents: true
            },
            minZoom: APP_CONFIG.zoom.min,
            maxZoom: APP_CONFIG.zoom.max,
            wheelSensitivity: 0.2, // Reduce wheel sensitivity for smoother zooming
            pixelRatio: 'auto', // Use device pixel ratio for better rendering
            textureOnViewport: false, // Disable texture on viewport for better edge rendering
            hideEdgesOnViewport: false, // Important: Don't hide edges during viewport changes
            hideLabelsOnViewport: true, // Hide labels during viewport changes for better performance
            motionBlur: false, // Disable motion blur for clearer edges
            motionBlurOpacity: 0.8,
            renderer: {
                // Force hardware acceleration
                hoverDelay: 0,
                hideEdgesOnViewport: false
            }
        });
        
        cyRef.current = cy;

            // Add node click event
            cy.on('tap', 'node', function(evt) {
                const node = evt.target;
                selectNode(node, evt.originalEvent.ctrlKey);
            });

            // Add edge click event
            cy.on('tap', 'edge', function(evt) {
                const edge = evt.target;
                selectEdge(edge);
            });

            // Add background click event to clear selection
            cy.on('tap', function(evt) {
                if (evt.target === cy) {
                    handleClearSelection();
                }
            });

            // Add zoom event listener to ensure edges remain visible
            cy.on('zoom', function() {
                // Force edges to be visible during zoom
                cy.edges().style({
                    'display': 'element',
                    'visibility': 'visible',
                    'opacity': 1,
                    'z-index': 999
                });
            });

            // Add viewport change event listener
            cy.on('viewport', function() {
                // Ensure edges are visible after viewport changes
                setTimeout(() => {
                    cy.edges().style({
                        'display': 'element',
                        'visibility': 'visible',
                        'opacity': 1,
                        'z-index': 999
                    });
                }, 50);
            });

        // Fetch and load data
        const loadData = async () => {
            setLoading(true);
            try {
                let tablesData;
                let fieldRelationshipsData;

                // Determine API base URL with better fallback handling
                // Try local CORS proxy first, then Lambda API directly
                const localProxyUrl = 'http://localhost:8080';
                const lambdaApiUrl = 'https://2w4qrncaf1.execute-api.us-east-1.amazonaws.com/dev';
                
                console.log('Loading data from API...');
                
                // Helper function to fetch with fallback
                const fetchWithFallback = async (endpoint) => {
                    // Configure fetch options with CORS headers
                    const fetchOptions = {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        mode: 'cors',
                        credentials: 'omit' // Important: 'omit' instead of 'include' for CORS
                    };
                    
                    // Try Lambda API directly first
                    try {
                        const url = `${lambdaApiUrl}${endpoint}`;
                        console.log(`Fetching directly from Lambda API: ${url}`);
                        
                        const response = await fetch(url, fetchOptions);
                        if (!response.ok) {
                            throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
                        }
                        
                        const data = await response.json();
                        console.log(`Successfully fetched data from Lambda API: ${endpoint}`);
                        return data;
                    } catch (lambdaError) {
                        console.error(`Error fetching from Lambda API (${endpoint}):`, lambdaError);
                        
                        // If Lambda API fails, try local server as fallback
                        try {
                            const localServerUrl = `http://localhost:5001${endpoint}`;
                            console.log(`Trying local server: ${localServerUrl}`);
                            
                            const localResponse = await fetch(localServerUrl, {
                                ...fetchOptions,
                                credentials: 'omit'
                            });
                            
                            if (!localResponse.ok) {
                                throw new Error(`Local server HTTP error ${localResponse.status}: ${localResponse.statusText}`);
                            }
                            
                            const data = await localResponse.json();
                            console.log(`Successfully fetched data from local server: ${endpoint}`);
                            return data;
                        } catch (localServerError) {
                            console.error(`Local server failed (${endpoint}):`, localServerError);
                            
                            // Fall back to mock data as last resort
                            console.log('All API attempts failed, using mock data');
                            return await fallbackToMockData(endpoint.includes('tables') ? 'tables' : 'field-relationships');
                        }
                    }
                };
                
                // Fetch tables data
                tablesData = await fetchWithFallback('/api/graph/tables');
                
                // Fetch field relationships data
                fieldRelationshipsData = await fetchWithFallback('/api/graph/field-relationships');
                
                // Transform field relationships data into the expected format
                const transformedFieldRelationships = {};
                
                // Always treat the data as an array since Lambda returns array format
                if (Array.isArray(fieldRelationshipsData)) {
                    // Get tables data to build the relation map
                    const relationMap = new Map();
                    tablesData.edges.forEach(edge => {
                        relationMap.set(edge.label, {
                            parent: edge.source,
                            child: edge.target
                        });
                    });

                    // Process field relationships
                    fieldRelationshipsData.forEach(rel => {
                        if (!rel.RelationID || !rel.ParentFieldName || !rel.ChildFieldName) return;
                        
                        // Get the parent and child tables from the relation map
                        const relation = relationMap.get(rel.RelationID);
                        if (!relation) return;
                        
                        const { parent, child } = relation;
                        
                        // Create a key for the table relationship
                        const relationshipKey = `${parent}-${child}`;
                        
                        // Initialize the array if it doesn't exist
                        if (!transformedFieldRelationships[relationshipKey]) {
                            transformedFieldRelationships[relationshipKey] = [];
                        }
                        
                        // Add the field relationship
                        transformedFieldRelationships[relationshipKey].push({
                            sourceField: `${parent}.${rel.ParentFieldName}`,
                            targetField: `${child}.${rel.ChildFieldName}`
                        });
                    });
                } else if (typeof fieldRelationshipsData === 'object') {
                    // If it's already in the expected object format, use it directly
                    Object.assign(transformedFieldRelationships, fieldRelationshipsData);
                }
                
                // Store transformed field relationships globally
                window.fieldRelationships = transformedFieldRelationships;
                
                // Process and add the nodes
                if (tablesData && tablesData.nodes && tablesData.edges) {
                    // Calculate container dimensions
                    const containerHeight = containerRef.current ? containerRef.current.clientHeight : window.innerHeight - 300;
                    setGraphHeight(`${containerHeight}px`);
                    
                    // Add nodes
                    cy.add(
                        tablesData.nodes.map(node => ({
                            data: {
                                id: node.id,
                                label: node.label,
                                connections: node.connections || 0
                            }
                        }))
                    );
                    
                    // Add edges
                    cy.add(
                        tablesData.edges.map(edge => ({
                            data: {
                                id: `${edge.source}-${edge.target}`,
                                source: edge.source,
                                target: edge.target,
                                label: edge.label || ''
                            }
                        }))
                    );
                    
                    // Apply layout with reduced padding and full width
                    cy.layout({
                        name: 'fcose',
                        fit: true,
                        padding: 10, // Reduce padding to use more space
                        nodeDimensionsIncludeLabels: true,
                        randomize: true,
                        nodeRepulsion: 8000,
                        idealEdgeLength: 80,
                        edgeElasticity: 0.45,
                        nestingFactor: 0.1,
                        gravity: 0.25,
                        numIter: 2500,
                        tile: true,
                        animate: 'end',
                        animationDuration: 500,
                        refresh: 20,
                        quality: 'proof',
                        packComponents: true
                    }).run();
                    
                    // Explicitly ensure all edges are visible after layout completes
                    setTimeout(() => {
                        cy.edges().style({
                            'display': 'element',
                            'visibility': 'visible',
                            'opacity': 1,
                            'line-color': '#ccc',
                            'target-arrow-color': '#ccc',
                            'z-index': 999 // Add z-index to ensure edges are rendered on top
                        });
                        
                        // Hide loading indicator
                        document.getElementById('loading').style.display = 'none';
                        
                        // Show success toast
                        showToast('Graph data loaded successfully');
                        
                        // Add a second check after a longer delay
                        setTimeout(() => {
                            if (cyRef.current) {
                                cyRef.current.edges().style({
                                    'display': 'element',
                                    'visibility': 'visible',
                                    'opacity': 1,
                                    'z-index': 999
                                });
                            }
                        }, 1000);
                    }, 1000); // Increased from 600ms to 1000ms to give layout more time to complete
                } else {
                    throw new Error('Invalid graph data format');
                }
            } catch (error) {
                console.error('Error loading data:', error);
                    document.getElementById('loading').style.display = 'none';
                    showToast('Error loading graph data', 'error');
            }
        };

        loadData();
        };
        
        // Use a small timeout to ensure the DOM is fully rendered
        const timeoutId = setTimeout(initCytoscape, 100);

        // Cleanup
        return () => {
            clearTimeout(timeoutId);
            if (cyRef.current) {
                cyRef.current.destroy();
            }
        };
    }, []);

    // Add a periodic check to ensure edges remain visible
    useEffect(() => {
        // Function to ensure edges are visible
        const ensureEdgesVisible = () => {
            if (cyRef.current) {
                cyRef.current.edges().style({
                    'display': 'element',
                    'visibility': 'visible',
                    'opacity': 1
                });
            }
        };
        
        // Run the check immediately after component mounts
        ensureEdgesVisible();
        
        // Set up an interval to periodically check edge visibility
        const intervalId = setInterval(ensureEdgesVisible, 2000);
        
        // Clean up the interval when component unmounts
        return () => clearInterval(intervalId);
    }, []);

    // Add zoom event handler to ensure edges remain visible during zoom
    useEffect(() => {
        if (!cyRef.current) return;
        
        const cy = cyRef.current;
        
        // Function to ensure edges are visible after zoom
        const onZoom = () => {
            const currentZoom = cy.zoom();
            
            // Apply automatic edge fixing based on zoom level
            autoFixEdgesOnZoom(currentZoom);
            
            // Basic edge visibility enforcement
            cy.edges().style({
                'display': 'element',
                'visibility': 'visible',
                'opacity': 1,
                'z-index': 999
            });
            
            // Force a style update to ensure changes are applied
            cy.style().update();
        };
        
        // Track zoom level to detect significant changes
        let lastZoomLevel = cy.zoom();
        
        // Add event listeners for zoom
        cy.on('zoom', () => {
            // Get current zoom level
            const currentZoom = cy.zoom();
            
            // Check if zoom level has changed significantly
            const zoomDelta = Math.abs(currentZoom - lastZoomLevel);
            if (zoomDelta > 0.3) {
                // If significant zoom change, call the more aggressive fix
                handleFixEdgeVisibility();
                lastZoomLevel = currentZoom;
            } else {
                // For minor zoom changes, just update edge styles
                onZoom();
                lastZoomLevel = currentZoom;
            }
        });
        
        cy.on('pan', onZoom);
        
        // Also add a debounced version for better performance
        let zoomTimeout;
        cy.on('zoom', () => {
            clearTimeout(zoomTimeout);
            zoomTimeout = setTimeout(() => {
                onZoom();
            }, 100);
        });
        
        return () => {
            // Clean up event listeners
            cy.off('zoom');
            cy.off('pan', onZoom);
            clearTimeout(zoomTimeout);
        };
    }, []);

    // Event handlers
    const handleSearch = (event) => {
        const searchTerm = event.target.value.toLowerCase();
        const resultsContainer = document.getElementById('search-results');
        resultsContainer.innerHTML = '';
        
        if (searchTerm.length < 2) return;
        
        const cy = cyRef.current;
        if (!cy) return;
        
        const matchingNodes = cy.nodes().filter(node => {
            const nodeLabel = node.data('label').toLowerCase();
            return nodeLabel.includes(searchTerm);
        });
        
        if (matchingNodes.length === 0) {
            resultsContainer.innerHTML = '<div class="no-results">No results found</div>';
            return;
        }
        
        // Sort nodes by connection count (highest first)
        const sortedNodes = matchingNodes.sort((a, b) => {
            return (b.data('connections') || 0) - (a.data('connections') || 0);
        });
        
        sortedNodes.forEach(node => {
            const resultItem = document.createElement('div');
            resultItem.className = 'search-result';
            resultItem.textContent = `${node.data('label')} (${node.data('connections') || 0})`;
            resultItem.addEventListener('click', () => {
                selectNode(node);
                resultsContainer.innerHTML = '';
                // Clear the search input
                event.target.value = '';
            });
            resultsContainer.appendChild(resultItem);
        });
    };

    // Find all paths between two nodes
    const findAllPaths = (startNode, endNode, maxDepth = 5) => {
        const cy = cyRef.current;
        if (!cy || !startNode || !endNode) return [];
        
        const paths = [];
        const visited = new Set();
        
        const dfs = (currentNode, currentPath = [], depth = 0) => {
            if (depth > maxDepth) return;
            if (currentNode.id() === endNode.id()) {
                paths.push([...currentPath, currentNode]);
                return;
            }
            
            visited.add(currentNode.id());
            
            const neighbors = currentNode.neighborhood('node');
            neighbors.forEach(neighbor => {
                if (!visited.has(neighbor.id())) {
                    dfs(neighbor, [...currentPath, currentNode], depth + 1);
                }
            });
            
            visited.delete(currentNode.id());
        };
        
        dfs(startNode);
        return paths;
    };

    // Analyze relationships along a path
    const analyzePathRelationships = (path) => {
        const relationships = [];
        for (let i = 0; i < path.length - 1; i++) {
            const currentNode = path[i];
            const nextNode = path[i + 1];
            
            // Find edges between these nodes
            const edges = cyRef.current.edges().filter(edge => {
                const source = edge.source().id();
                const target = edge.target().id();
                return (source === currentNode.id() && target === nextNode.id()) ||
                       (source === nextNode.id() && target === currentNode.id());
            });
            
            // Get field relationships
            edges.forEach(edge => {
                const sourceId = edge.source().id();
                const targetId = edge.target().id();
                const relationshipKey = `${sourceId}-${targetId}`;
                const reverseRelationshipKey = `${targetId}-${sourceId}`;
                
                const fieldRelationships = window.fieldRelationships && 
                    (window.fieldRelationships[relationshipKey] || 
                     window.fieldRelationships[reverseRelationshipKey]);
                
                relationships.push({
                    source: edge.source(),
                    target: edge.target(),
                    edge: edge,
                    fieldRelationships: fieldRelationships || []
                });
            });
        }
        return relationships;
    };

    // Modified selectNode to handle multi-select
    const selectNode = (node, isCtrlPressed = false) => {
        const cy = cyRef.current;
        if (!cy || !node) return;

        // Get the Hide unselected checkbox state
        const hideUnselectedCheckbox = document.querySelector('input[type="checkbox"][name="hideUnselected"]');
        const hideUnselected = hideUnselectedCheckbox && hideUnselectedCheckbox.checked;

        if (isCtrlPressed) {
            // Multi-select mode
            if (selectedNodes.has(node.id())) {
                // If node is already selected, unselect it
                selectedNodes.delete(node.id());
                node.style({
                    'background-color': APP_CONFIG.colors.base,
                    'opacity': hideUnselected ? 0.3 : 1
                });
                
                // Update UI for remaining selected node if any
                if (selectedNodes.size === 1) {
                    const remainingNodeId = Array.from(selectedNodes)[0];
                    const remainingNode = cy.getElementById(remainingNodeId);
                    document.getElementById('selected-name').textContent = remainingNode.data('label');
                    document.getElementById('selected-connections').textContent = 
                        `${remainingNode.connectedEdges().length} connections`;
                }
            } else {
                if (selectedNodes.size === 2) {
                    // If we already have 2 nodes selected, remove the most recently added one
                    const nodeIds = Array.from(selectedNodes);
                    const nodeToRemove = cy.getElementById(nodeIds[1]); // Remove second node
                    selectedNodes.delete(nodeIds[1]);
                    nodeToRemove.style({
                        'background-color': APP_CONFIG.colors.base,
                        'opacity': hideUnselected ? 0.3 : 1
                    });
                }
                
                // Add the new node to selection
                selectedNodes.add(node.id());
                node.style({
                    'background-color': APP_CONFIG.colors.selected,
                    'opacity': 1,
                    'z-index': 9999
                });
            }

            setSelectedNodes(new Set(selectedNodes));

            // Update UI based on number of selected nodes
            if (selectedNodes.size === 0) {
                document.getElementById('selected-name').textContent = 'None';
                document.getElementById('selected-connections').textContent = '0';
                document.getElementById('edge-info').style.display = 'none';
            } else if (selectedNodes.size === 1) {
                const selectedNodeId = Array.from(selectedNodes)[0];
                const selectedNode = cy.getElementById(selectedNodeId);
                document.getElementById('selected-name').textContent = selectedNode.data('label');
                document.getElementById('selected-connections').textContent = 
                    `${selectedNode.connectedEdges().length} connections`;
                document.getElementById('edge-info').style.display = 'none';
            } else if (selectedNodes.size === 2) {
                const [firstNodeId, secondNodeId] = Array.from(selectedNodes);
                const firstNode = cy.getElementById(firstNodeId);
                const secondNode = cy.getElementById(secondNodeId);
                document.getElementById('selected-name').textContent = 
                    `${firstNode.data('label')} ↔ ${secondNode.data('label')}`;
                document.getElementById('selected-connections').textContent = 
                    'Finding paths between nodes...';
                document.getElementById('edge-info').style.display = 'none';
            }

            // If exactly 2 nodes are selected, find and highlight paths between them
            if (selectedNodes.size === 2) {
                const [startId, endId] = Array.from(selectedNodes);
                const startNode = cy.getElementById(startId);
                const endNode = cy.getElementById(endId);

                // Reset all nodes and edges to base state first
                cy.elements().style({
                    'opacity': hideUnselected ? 0.3 : 1,
                    'background-color': APP_CONFIG.colors.base,
                    'line-color': '#ccc',
                    'target-arrow-color': '#ccc'
                });

                // Find all paths between the nodes
                const allPaths = findAllPaths(startNode, endNode);

                if (allPaths.length === 0) {
                    showToast('No paths found between selected nodes', 'warning');
                    document.getElementById('selected-connections').textContent = 'No paths found';
                    setRelationships([]);
                    return;
                }

                // Update the connections count
                document.getElementById('selected-connections').textContent = 
                    `Found ${allPaths.length} path${allPaths.length > 1 ? 's' : ''}`;

                // Highlight paths
                allPaths.forEach(path => {
                    path.forEach(pathNode => {
                        if (!selectedNodes.has(pathNode.id())) {
                            pathNode.style({
                                'background-color': APP_CONFIG.colors.connected[0],
                                'opacity': 1
                            });
                        }
                    });

                    for (let i = 0; i < path.length - 1; i++) {
                        const currentNode = path[i];
                        const nextNode = path[i + 1];
                        cy.edges().filter(edge => {
                            const source = edge.source().id();
                            const target = edge.target().id();
                            return (source === currentNode.id() && target === nextNode.id()) ||
                                   (source === nextNode.id() && target === currentNode.id());
                        }).style({
                            'line-color': APP_CONFIG.colors.highlightedEdge,
                            'target-arrow-color': APP_CONFIG.colors.highlightedEdge,
                            'opacity': 1
                        });
                    }
                });

                // Ensure selected nodes are highlighted in red
                selectedNodes.forEach(nodeId => {
                    cy.getElementById(nodeId).style({
                        'background-color': APP_CONFIG.colors.selected,
                        'opacity': 1,
                        'z-index': 9999
                    });
                });

                // Build and display relationships for each path
                const pathRelationships = allPaths.map((path, pathIndex) => {
                    const relationships = analyzePathRelationships(path);
                    
                    // Group relationships by node pairs
                    const nodeGroups = [];
                    for (let i = 0; i < path.length - 1; i++) {
                        const currentNode = path[i];
                        const nextNode = path[i + 1];
                        
                        // Find relationships between these nodes
                        const nodeRelationships = relationships.filter(rel => 
                            (rel.source.id() === currentNode.id() && rel.target.id() === nextNode.id()) ||
                            (rel.source.id() === nextNode.id() && rel.target.id() === currentNode.id())
                        );
                        
                        if (nodeRelationships.length > 0) {
                            nodeGroups.push({
                                depth: i + 1,
                                nodeId: `${currentNode.id()}-${nextNode.id()}`,
                                label: `${currentNode.data('label')} ↔ ${nextNode.data('label')}`,
                                relationships: nodeRelationships
                            });
                        }
                    }

                    return {
                        nodeId: `path-${pathIndex + 1}`,
                        label: `Path ${pathIndex + 1} (${path.length} nodes)`,
                        connectedNodeGroups: nodeGroups,
                        nodes: path.map(n => n.data('label')).join(' → ')
                    };
                });

                setRelationships(pathRelationships);
        } else {
                // Reset styles if we don't have exactly 2 nodes selected
                cy.elements().style({
                    'opacity': hideUnselected ? 0.3 : 1,
                    'background-color': APP_CONFIG.colors.base,
                    'line-color': '#ccc',
                    'target-arrow-color': '#ccc'
                });

                // Re-highlight selected nodes
                selectedNodes.forEach(nodeId => {
                    cy.getElementById(nodeId).style({
                        'background-color': APP_CONFIG.colors.selected,
                        'opacity': 1,
                        'z-index': 9999
                    });
                });

                setRelationships([]);
            }
        } else {
            // Single-select mode
            // Clear previous selection
            selectedNodes.clear();
            selectedNodes.add(node.id());
            setSelectedNodes(new Set(selectedNodes));

            // Reset all node colors and opacity
            cy.elements().style({
                'opacity': hideUnselected ? 0.3 : 1,
                'background-color': APP_CONFIG.colors.base,
                'line-color': '#ccc',
                'target-arrow-color': '#ccc'
            });

            // Get the current depth setting
            const depth = parseInt(document.querySelector('input[type="number"]').value || '1', 10);

            // Get all nodes connected to the selected node at the specified depth
            const connectedNodes = getNodesAtDepth(node, depth);
            
            // Update the selected node info in the UI
            document.getElementById('selected-name').textContent = node.data('label');
            const visibleConnections = connectedNodes.size - 1; // Subtract 1 to exclude the selected node
            document.getElementById('selected-connections').textContent = 
                `${visibleConnections} connection${visibleConnections !== 1 ? 's' : ''} at depth ${depth}`;
            document.getElementById('edge-info').style.display = 'none';

            // Highlight connected nodes
            connectedNodes.forEach(connectedNode => {
                if (connectedNode.id() !== node.id()) {
                    connectedNode.style({
                        'background-color': APP_CONFIG.colors.connected[0],
                        'opacity': 1
                    });
                }
            });

            // Highlight edges between connected nodes
            cy.edges().filter(edge => {
                const source = edge.source();
                const target = edge.target();
                return connectedNodes.has(source) && connectedNodes.has(target);
            }).style({
                'line-color': APP_CONFIG.colors.highlightedEdge,
                'target-arrow-color': APP_CONFIG.colors.highlightedEdge,
                'opacity': 1
            });

            // Build relationships for the selected node
            const relationships = buildRelationships(node, 1, depth);

            // Group relationships by depth and connected node
            const groupedRelationships = relationships.reduce((acc, rel) => {
                const depth = rel.depth;
                const connectedNode = rel.source.id() === node.id() ? rel.target : rel.source;
                const key = `${depth}-${connectedNode.id()}`;

                if (!acc[key]) {
                    acc[key] = {
                        depth,
                        nodeId: connectedNode.id(),
                        label: connectedNode.data('label'),
                        relationships: []
                    };
                }
                acc[key].relationships.push(rel);
                return acc;
            }, {});

            // Convert to array and sort by depth and label
            const sortedGroups = Object.values(groupedRelationships).sort((a, b) => {
                if (a.depth !== b.depth) return a.depth - b.depth;
                return a.label.localeCompare(b.label);
            });

            setRelationships([{
                nodeId: node.id(),
                label: node.data('label'),
                connectedNodeGroups: sortedGroups
            }]);

            // IMPORTANT: Ensure the selected node is highlighted in red AFTER all other operations
        node.style({
            'background-color': APP_CONFIG.colors.selected,
            'opacity': 1,
            'z-index': 9999
        });
        }
    };

    const selectEdge = (edge) => {
        const cy = cyRef.current;
        if (!cy) return;
        
        // Clear previous selection
        cy.elements().removeClass('highlighted');
        cy.nodes().unselect();
        cy.edges().unselect();
        
        // Select the edge
        edge.select();
        
        // Get source and target nodes
        const sourceNode = edge.source();
        const targetNode = edge.target();
        
        // Highlight the edge and its connected nodes
        edge.addClass('highlighted');
        sourceNode.addClass('highlighted');
        targetNode.addClass('highlighted');
        
        // Update the selected node info
        document.getElementById('selected-name').textContent = 'Edge Relationship';
        document.getElementById('selected-connections').textContent = 
            `${sourceNode.data('label')} → ${targetNode.data('label')}`;
        
        // Show edge relationship info
        const edgeInfoElement = document.getElementById('edge-info');
        edgeInfoElement.style.display = 'block';
        
        // Get field relationships if available
        let relationshipHtml = '<h4>Field Relationships:</h4>';
        
        if (window.fieldRelationships) {
            const sourceId = sourceNode.id();
            const targetId = targetNode.id();
            const relationshipKey = `${sourceId}-${targetId}`;
            const reverseRelationshipKey = `${targetId}-${sourceId}`;
            
            const relationships = window.fieldRelationships[relationshipKey] || 
                                window.fieldRelationships[reverseRelationshipKey] || [];
            
            if (relationships.length > 0) {
                relationshipHtml += '<ul class="field-relationships">';
                relationships.forEach(rel => {
                    relationshipHtml += `<li><span class="source-field">${rel.sourceField}</span> → <span class="target-field">${rel.targetField}</span></li>`;
                });
                relationshipHtml += '</ul>';
            } else {
                relationshipHtml += '<p>No detailed field relationships available</p>';
            }
        } else {
            relationshipHtml += '<p>Field relationship data not loaded</p>';
        }
        
        edgeInfoElement.innerHTML = relationshipHtml;
        
        // Center on the edge
        cy.animate({
            center: {
                eles: edge
            },
            duration: 500
        });
        
        showToast(`Edge selected: ${sourceNode.data('label')} → ${targetNode.data('label')}`);
    };

    const handleClearSelection = () => {
        const cy = cyRef.current;
        if (!cy) return;
        
        // Clear all highlighting and selection
        cy.elements().removeClass('highlighted');
        cy.elements().removeClass('dimmed');
        cy.nodes().unselect();
        
        // Reset node colors to default
        cy.nodes().style('background-color', APP_CONFIG.colors.base);
        
        // Reset edge colors
        cy.edges().style({
            'line-color': '#ccc',
            'target-arrow-color': '#ccc'
        });
        
        // Reset selected node info
        document.getElementById('selected-name').textContent = 'None';
        document.getElementById('selected-connections').textContent = '0';
        
        // Hide edge relationship info
        document.getElementById('edge-info').style.display = 'none';
        
        // Reset any checkboxes
        const hideUnselectedCheckbox = document.querySelector('input[type="checkbox"]');
        if (hideUnselectedCheckbox) {
            hideUnselectedCheckbox.checked = false;
        }
        
        showToast('Selection cleared');
    };

    const handleCenterView = () => {
        if (cyRef.current) {
            cyRef.current.animate({
                fit: {
                    padding: 50
                },
                duration: 500
            });
        }
    };
    
    // Add a function to fix edge visibility issues
    const handleFixEdgeVisibility = () => {
        if (!cyRef.current) return;
        
        // Show loading indicator
        const loadingElement = document.getElementById('loading');
        if (loadingElement) {
            loadingElement.style.display = 'block';
            loadingElement.textContent = 'Fixing edge visibility...';
        }
        
        // Force redraw of all edges with increased visibility settings
        cyRef.current.edges().style({
            'display': 'element',
            'visibility': 'visible',
            'opacity': 1,
            'z-index': 999,
            'line-color': '#ccc',
            'target-arrow-color': '#ccc',
            'width': 2,
            'curve-style': 'bezier',
            'arrow-scale': 1.5,
            'text-opacity': 1,
            'text-outline-width': 2,
            'text-outline-color': '#fff',
            'text-background-opacity': 0.7,
            'text-background-color': '#fff',
            'text-background-padding': 2,
            'edge-text-rotation': 'autorotate',
            'min-zoomed-font-size': 8
        });
        
        // Force a complete redraw of the graph
        cyRef.current.style().update();
        
        // Force a layout refresh without changing positions
        cyRef.current.layout({
            name: 'preset',
            fit: false,
            animate: false
        }).run();
        
        // Force a viewport update
        cyRef.current.viewport({
            zoom: cyRef.current.zoom(),
            pan: cyRef.current.pan()
        });
        
        // Add a second check after a short delay
        setTimeout(() => {
            if (cyRef.current) {
                cyRef.current.edges().style({
                    'display': 'element',
                    'visibility': 'visible',
                    'opacity': 1,
                    'z-index': 999
                });
                
                // Force another redraw
                cyRef.current.style().update();
            }
            
            // Hide loading indicator
            if (loadingElement) {
                loadingElement.style.display = 'none';
            }
            showToast('Edge visibility fixed');
        }, 500);
    };
    
    const handleShowHeavyNodes = () => {
        const cy = cyRef.current;
        if (!cy) return;
        
        // Clear previous highlights
        cy.elements().removeClass('highlighted');
        
        // Find nodes with more than 6 connections
        const heavyNodes = cy.nodes().filter(node => {
            const connections = node.connectedEdges().length;
            return connections > 6;
        });
        
        if (heavyNodes.length === 0) {
            showToast('No nodes with more than 6 connections found', 'warning');
            return;
        }
        
        // Highlight heavy nodes
        heavyNodes.forEach((node, i) => {
            const colorIndex = i % APP_CONFIG.colors.connected.length;
            node.style('background-color', APP_CONFIG.colors.connected[colorIndex]);
        });
        
        // Highlight edges between heavy nodes
        const edgesToHighlight = cy.edges().filter(edge => {
            const sourceId = edge.source().id();
            const targetId = edge.target().id();
            return heavyNodes.some(n => n.id() === sourceId) && 
                   heavyNodes.some(n => n.id() === targetId);
        });
        
        edgesToHighlight.addClass('highlighted');
        
        showToast(`Found ${heavyNodes.length} nodes with more than 6 connections`);
    };
    
    const handleHideUnselected = (e) => {
        const cy = cyRef.current;
        if (!cy) return;
        
        const hideUnselected = e.target.checked;
        
        if (hideUnselected) {
            // Check if any nodes are selected using our React state
            if (selectedNodes.size === 0) {
                showToast('Please select a node first', 'warning');
                e.target.checked = false;
                return;
            }
            
            // Get the selected node from our state
            const selectedNodeId = Array.from(selectedNodes)[0];
            const selectedNode = cy.getElementById(selectedNodeId);
            if (!selectedNode || selectedNode.length === 0) {
                showToast('Selected node not found', 'warning');
                e.target.checked = false;
                return;
            }
            
            // Get the current depth setting
            const depth = parseInt(document.querySelector('input[type="number"]').value || '1', 10);
            
            // Get all nodes connected to the selected node at the specified depth
            const connectedNodesSet = getNodesAtDepth(selectedNode, depth);
            
            // Convert the Set to an array of node IDs
            const connectedNodeIds = Array.from(connectedNodesSet).map(node => node.id());
            
            // Create a collection of connected nodes
            const connectedNodesCollection = cy.collection();
            connectedNodeIds.forEach(id => {
                const node = cy.getElementById(id);
                if (node.length > 0) {
                    connectedNodesCollection.merge(node);
                }
            });
            
            // Make sure the selected node and connected nodes are always visible
            connectedNodesCollection.style({
                'opacity': 1,
                'z-index': 999
            });
            
            // Get edges between connected nodes
            const connectedEdges = cy.edges().filter(edge => {
                const source = edge.source();
                const target = edge.target();
                return connectedNodeIds.includes(source.id()) && connectedNodeIds.includes(target.id());
            });
            
            // Reduce opacity of unselected nodes that are not connected
            cy.nodes().not(connectedNodesCollection).style({
                'opacity': 0.2
            });
            
            // Reduce opacity of edges not connected to selected nodes
            cy.edges().not(connectedEdges).style({
                'opacity': 0.1
            });
            
            showToast('Dimming unselected nodes');
            
            // IMPORTANT: Ensure the selected node is highlighted in red AFTER all other operations
            selectedNode.style({
                'background-color': APP_CONFIG.colors.selected,
                'opacity': 1,
                'z-index': 9999
            });
        } else {
            // If we're in multi-select mode (2 nodes selected), maintain path visibility
            if (selectedNodes.size === 2) {
                const [startId, endId] = Array.from(selectedNodes);
                const startNode = cy.getElementById(startId);
                const endNode = cy.getElementById(endId);
                
                // Find all paths between the nodes
                const allPaths = findAllPaths(startNode, endNode);
                
                // Reset all nodes and edges to base state first
                cy.elements().style({
                    'opacity': 0.3,
                    'background-color': APP_CONFIG.colors.base,
                    'line-color': '#ccc',
                    'target-arrow-color': '#ccc'
                });
                
                // Highlight paths
                allPaths.forEach(path => {
                    path.forEach(pathNode => {
                        if (!selectedNodes.has(pathNode.id())) {
                            pathNode.style({
                                'background-color': APP_CONFIG.colors.connected[0],
                'opacity': 1
            });
                        }
                    });
                    
                    for (let i = 0; i < path.length - 1; i++) {
                        const currentNode = path[i];
                        const nextNode = path[i + 1];
                        cy.edges().filter(edge => {
                            const source = edge.source().id();
                            const target = edge.target().id();
                            return (source === currentNode.id() && target === nextNode.id()) ||
                                   (source === nextNode.id() && target === currentNode.id());
                        }).style({
                            'line-color': APP_CONFIG.colors.highlightedEdge,
                            'target-arrow-color': APP_CONFIG.colors.highlightedEdge,
                'opacity': 1
                        });
                    }
            });
            
                // Ensure selected nodes are highlighted in red
                selectedNodes.forEach(nodeId => {
                    cy.getElementById(nodeId).style({
                'background-color': APP_CONFIG.colors.selected,
                'opacity': 1,
                'z-index': 9999
                    });
                });
            } else {
                // For single select or no selection, restore full visibility
                cy.elements().style({
                    'opacity': 1
                });
                
                // If we have a single selected node, maintain its highlighting
                if (selectedNodes.size === 1) {
                    const selectedNodeId = Array.from(selectedNodes)[0];
                    const selectedNode = cy.getElementById(selectedNodeId);
                    if (selectedNode && selectedNode.length > 0) {
                        const depth = parseInt(document.querySelector('input[type="number"]').value || '1', 10);
                        const connectedNodes = getNodesAtDepth(selectedNode, depth);
                        
                        // Highlight the selected node
                        selectedNode.style({
                            'background-color': APP_CONFIG.colors.selected,
                            'opacity': 1,
                            'z-index': 9999
                        });
                        
                        // Highlight connected nodes
                        connectedNodes.forEach(connectedNode => {
                            if (connectedNode.id() !== selectedNode.id()) {
                                connectedNode.style({
                                    'background-color': APP_CONFIG.colors.connected[0],
                                    'opacity': 1
                                });
                            }
                        });
                        
                        // Highlight edges between connected nodes
                        cy.edges().filter(edge => {
                            const source = edge.source();
                            const target = edge.target();
                            return connectedNodes.has(source) && connectedNodes.has(target);
                        }).style({
                            'line-color': APP_CONFIG.colors.highlightedEdge,
                            'target-arrow-color': APP_CONFIG.colors.highlightedEdge,
                            'opacity': 1
                        });
                    }
                }
            }
            
            showToast('Showing all nodes');
        }
    };
    
    const highlightNeighborhood = (node, depth) => {
        const cy = cyRef.current;
        if (!cy || !node) return;
        
        // Clear previous highlights
        cy.elements().removeClass('highlighted');
        cy.nodes().style('background-color', '');
        cy.edges().style('line-color', '');
        
        // Add highlighted class to the selected node
        node.select();
        
        // Get all nodes connected to the selected node at the specified depth
        const connectedNodes = getNodesAtDepth(node, depth);
        
        // Highlight connected nodes and edges
        connectedNodes.forEach(connectedNode => {
            // Don't add highlighted class to the selected node
            if (connectedNode.id() !== node.id()) {
                connectedNode.addClass('highlighted');
            }
        });
        
        // Highlight edges between highlighted nodes
        cy.edges().forEach(edge => {
            const source = edge.source();
            const target = edge.target();
            
            if (source.hasClass('highlighted') && target.hasClass('highlighted')) {
                edge.addClass('highlighted');
                edge.style('line-color', APP_CONFIG.colors.highlightedEdge);
            }
        });
        
        // IMPORTANT: Set the selected node style AFTER all other operations
        // to ensure it stays red and doesn't get overridden
        node.style({
            'background-color': APP_CONFIG.colors.selected,
            'opacity': 1,
            'z-index': 9999
        });
    };
    
    // Get nodes at a specific depth from the start node, ignoring direction
    const getNodesAtDepth = (startNode, targetDepth, currentDepth = 0, visited = new Set()) => {
        const cy = cyRef.current;
        if (!cy || !startNode) return new Set();
        
        if (currentDepth > targetDepth) return new Set();
        
        // Mark the current node as visited
        visited.add(startNode.id());
        
        // Initialize the set with the current node
        const connectedNodes = new Set([startNode]);
        
        // If we haven't reached the target depth yet, continue traversing
        if (currentDepth < targetDepth) {
            // Get all edges connected to this node (both incoming and outgoing)
            const connectedEdges = startNode.connectedEdges();
            
            // For each connected edge, get the node on the other end
            connectedEdges.forEach(edge => {
                const sourceId = edge.source().id();
                const targetId = edge.target().id();
                
                // Get the node on the other end of the edge
                const neighborId = sourceId === startNode.id() ? targetId : sourceId;
                const neighborNode = cy.getElementById(neighborId);
                
                // If we haven't visited this node yet, traverse it
                if (!visited.has(neighborId)) {
                    // Recursively get nodes at the next depth and add them to our set
                    const nodesAtNextDepth = getNodesAtDepth(neighborNode, targetDepth, currentDepth + 1, visited);
                    nodesAtNextDepth.forEach(node => connectedNodes.add(node));
                }
            });
        }
        
        return connectedNodes;
    };
    
    const handleDepthChange = (e) => {
        const cy = cyRef.current;
        if (!cy) return;

        const depth = parseInt(e.target.value, 10);
        if (isNaN(depth) || depth < 1 || depth > 5) {
            showToast('Please enter a valid depth between 1 and 5', 'warning');
            return;
        }
        
        // Get currently selected nodes from our state instead of cytoscape
        const selectedNodeIds = Array.from(selectedNodes);
        if (selectedNodeIds.length === 0) {
            showToast('Please select a node first', 'warning');
            // Reset the depth input to default value
            e.target.value = '1';
            return;
        }
        
        // Get the selected node from our tracked state
        const selectedNode = cy.getElementById(selectedNodeIds[0]);
        if (!selectedNode || selectedNode.length === 0) {
            showToast('Selected node not found', 'warning');
            return;
        }

        // Get the Hide unselected checkbox state
        const hideUnselectedCheckbox = document.querySelector('input[type="checkbox"][name="hideUnselected"]');
        const hideUnselected = hideUnselectedCheckbox && hideUnselectedCheckbox.checked;

        // Reset all node colors to default before applying new highlighting
        cy.elements().style({
            'opacity': hideUnselected ? 0.3 : 1,
            'background-color': APP_CONFIG.colors.base,
            'line-color': '#ccc',
            'target-arrow-color': '#ccc'
        });

        // Get connected nodes at the new depth
        const connectedNodes = getNodesAtDepth(selectedNode, depth);
        
        // Update the connections count to show visible connections at current depth
        const visibleConnections = connectedNodes.size - 1; // Subtract 1 to exclude the selected node
        document.getElementById('selected-connections').textContent = 
            `${visibleConnections} connection${visibleConnections !== 1 ? 's' : ''} at depth ${depth}`;

        // Build relationships for the selected node with new depth
        const relationships = buildRelationships(selectedNode, 1, depth);
        
        // Group relationships by depth and connected node
        const groupedRelationships = relationships.reduce((acc, rel) => {
            const depth = rel.depth;
            const connectedNode = rel.source.id() === selectedNode.id() ? rel.target : rel.source;
            const key = `${depth}-${connectedNode.id()}`;
            
            if (!acc[key]) {
                acc[key] = {
                    depth,
                    nodeId: connectedNode.id(),
                    label: connectedNode.data('label'),
                    relationships: []
                };
            }
            acc[key].relationships.push(rel);
            return acc;
        }, {});
        
        // Convert to array and sort by depth and label
        const sortedGroups = Object.values(groupedRelationships).sort((a, b) => {
            if (a.depth !== b.depth) return a.depth - b.depth;
            return a.label.localeCompare(b.label);
        });
        
        // Update the relationships state with the new data
        setRelationships([{
            nodeId: selectedNode.id(),
            label: selectedNode.data('label'),
            connectedNodeGroups: sortedGroups
        }]);

        // Highlight the selected node
        selectedNode.style({
            'background-color': APP_CONFIG.colors.selected,
            'opacity': 1
        });
        
        // Highlight connected nodes and their edges
        connectedNodes.forEach(connectedNode => {
            if (connectedNode.id() !== selectedNode.id()) {
                connectedNode.style({
                    'background-color': APP_CONFIG.colors.connected[0],
                    'opacity': 1
                });
            }
        });

        // Highlight edges between connected nodes
        cy.edges().filter(edge => {
            const source = edge.source();
            const target = edge.target();
            return connectedNodes.has(source) && connectedNodes.has(target);
        }).style({
            'line-color': APP_CONFIG.colors.highlightedEdge,
            'target-arrow-color': APP_CONFIG.colors.highlightedEdge,
            'opacity': 1
        });
        
        showToast(`Updated node depth to ${depth}`);
    };

    // Handle dark mode toggle
    const handleDarkModeToggle = (e) => {
        const darkModeEnabled = e.target.checked;
        setIsDarkMode(darkModeEnabled);
        
        const cy = cyRef.current;
        if (!cy) return;
        
        // Update the background color of the container
        const container = document.getElementById('cy');
        if (container) {
            container.style.backgroundColor = darkModeEnabled ? '#222' : '#f8f9fa';
        }
        
        // Add or remove dark-mode class from body
        if (darkModeEnabled) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
        
        // Update the styles for nodes and edges
        cy.style()
            .selector('node')
            .style({
                'text-outline-color': darkModeEnabled ? '#333' : '#4a90e2',
                'color': darkModeEnabled ? '#fff' : '#fff',
                'border-color': darkModeEnabled ? '#444' : '#fff'
            })
            .selector('edge')
            .style({
                'line-color': darkModeEnabled ? '#555' : '#ccc',
                'target-arrow-color': darkModeEnabled ? '#555' : '#ccc',
                'text-background-color': darkModeEnabled ? '#333' : '#fff'
            })
            .update();
        
        showToast(`Dark mode ${darkModeEnabled ? 'enabled' : 'disabled'}`);
    };
    
    // Handle node separation change
    const handleNodeSeparationChange = (e) => {
        const newSeparation = parseInt(e.target.value, 10);
        setNodeSeparation(newSeparation);
        
        // Apply the current layout with the new separation value
        applyLayout(currentLayout, newSeparation);
        
        showToast(`Node separation set to ${newSeparation}`);
    };

    // Fallback function to get mock data when CORS errors occur
    const fallbackToMockData = async (dataType) => {
        console.log(`Attempting to fallback for ${dataType} data`);
        
        // Try to fetch from local server if available
        const localPorts = [5001, 3000]; // Try both ports
        
        for (const port of localPorts) {
            try {
                const localUrl = `http://localhost:${port}/api/graph/${dataType}`;
                console.log(`Attempting to fetch from local server: ${localUrl}`);
                
                const response = await fetch(localUrl, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    mode: 'cors',
                    credentials: 'omit'
                });
                
                if (response.ok) {
                    console.log(`Successfully fetched data from ${localUrl}`);
                    const data = await response.json();
                    return data;
                }
            } catch (error) {
                console.error(`Error fetching from localhost:${port}:`, error);
            }
        }
        
        console.log('All local server attempts failed, using hardcoded mock data');
        
        // If local server fails, return hardcoded mock data
        if (dataType === 'tables') {
            return {
                nodes: [
                    { id: 'GLBudgetHd', label: 'GLBudgetHd', connections: 5 },
                    { id: 'GLBudgetDtl', label: 'GLBudgetDtl', connections: 3 },
                    { id: 'GLAccount', label: 'GLAccount', connections: 8 }
                ],
                edges: [
                    { source: 'GLBudgetHd', target: 'GLBudgetDtl', label: 'BudgetRelation' },
                    { source: 'GLBudgetDtl', target: 'GLAccount', label: 'AccountRelation' }
                ]
            };
        } else if (dataType === 'field-relationships') {
            // For field-relationships, we can return either an array or object format
            // Here we're returning the array format that the Lambda would return
            return [
                {
                    RelationID: 'BudgetRelation',
                    ParentFieldName: 'BudgetID',
                    ChildFieldName: 'BudgetID',
                    ParentDataTableID: 'GLBudgetHd',
                    ChildDataTableID: 'GLBudgetDtl'
                },
                {
                    RelationID: 'AccountRelation',
                    ParentFieldName: 'AccountID',
                    ChildFieldName: 'AccountID',
                    ParentDataTableID: 'GLBudgetDtl',
                    ChildDataTableID: 'GLAccount'
                }
            ];
        }
        
        return dataType === 'tables' ? { nodes: [], edges: [] } : [];
    };

    // Add a function to apply different layouts
    const applyLayout = (layoutName, separation = nodeSeparation) => {
        if (!cyRef.current) return;
        
        setCurrentLayout(layoutName);
        
        const layoutOptions = {
            cose: {
                name: 'cose',
                fit: true,
                padding: 30,
                nodeRepulsion: 8000,
                idealEdgeLength: separation, // Use the separation parameter
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.25,
                numIter: 2500,
                tile: true,
                animate: 'end',
                animationDuration: 500
            },
            'cose-bilkent': {
                name: 'cose-bilkent',
                fit: true,
                padding: 10,
                nodeDimensionsIncludeLabels: true,
                randomize: true,
                nodeRepulsion: 8000,
                idealEdgeLength: separation, // Use the separation parameter
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.25,
                numIter: 2500,
                tile: true,
                animate: 'end',
                animationDuration: 500,
                refresh: 20,
                packComponents: true
            },
            fcose: {
                name: 'fcose',
                fit: true,
                padding: 30,
                nodeDimensionsIncludeLabels: true,
                randomize: true,
                nodeRepulsion: 8000,
                idealEdgeLength: separation,
                edgeElasticity: 0.45,
                nestingFactor: 0.1,
                gravity: 0.25,
                numIter: 2500,
                tile: true,
                animate: 'end',
                animationDuration: 500,
                quality: 'proof',
                packComponents: true
            },
            cola: {
                name: 'cola',
                fit: true,
                padding: 30,
                nodeDimensionsIncludeLabels: true,
                maxSimulationTime: 3000,
                nodeSpacing: separation / 2,
                edgeLength: separation,
                animate: 'end',
                animationDuration: 500,
                avoidOverlap: true,
                unconstrIter: 10,
                userConstIter: 15,
                allConstIter: 20
            },
            cise: {
                name: 'cise',
                fit: true,
                padding: 30,
                nodeDimensionsIncludeLabels: true,
                clusters: [], // Auto-detect clusters
                randomize: true,
                nodeRepulsion: separation * 10,
                idealInterClusterEdgeLengthCoefficient: 1.5,
                animate: 'end',
                animationDuration: 500,
                refresh: 20
            },
            elk: {
                name: 'elk',
                fit: true,
                padding: 30,
                nodeDimensionsIncludeLabels: true,
                elk: {
                    algorithm: 'layered',
                    'elk.direction': 'DOWN',
                    'elk.spacing.nodeNode': separation / 2,
                    'elk.layered.spacing.nodeNodeBetweenLayers': separation
                },
                animate: 'end',
                animationDuration: 500
            },
            dagre: {
                name: 'dagre',
                fit: true,
                padding: 30,
                rankDir: 'TB', // 'TB' for top to bottom, 'LR' for left to right
                rankSep: separation, // Use the separation parameter
                nodeSep: separation / 2, // Half of the separation for node spacing
                edgeSep: separation / 8 // Smaller value for edge spacing
            },
            klay: {
                name: 'klay',
                fit: true,
                padding: 30,
                klay: {
                    direction: 'DOWN',
                    spacing: separation / 2, // Use half of the separation parameter
                    nodeLayering: 'NETWORK_SIMPLEX'
                }
            },
            euler: {
                name: 'euler',
                fit: true,
                padding: 30,
                springLength: edge => separation, // Use the separation parameter
                springCoeff: edge => 0.0008,
                mass: node => 4,
                gravity: -1.2,
                pull: 0.001,
                theta: 0.666
            }
        };
        
        // Show loading indicator
        const loadingElement = document.getElementById('loading');
        if (loadingElement) {
            loadingElement.style.display = 'block';
            loadingElement.textContent = `Applying ${layoutName} layout...`;
        }
        
        // Apply the selected layout
        const layout = cyRef.current.layout(layoutOptions[layoutName] || layoutOptions['cose-bilkent']);
        
        // Run the layout and handle completion
        layout.run();
        
        // Hide loading indicator after layout completes
        layout.one('layoutstop', () => {
            if (loadingElement) {
                loadingElement.style.display = 'none';
            }
            
            // Ensure all edges are visible after layout completes
            setTimeout(() => {
                if (cyRef.current) {
                    cyRef.current.edges().style({
                        'display': 'element',
                        'visibility': 'visible',
                        'opacity': 1,
                        'z-index': 999 // Add z-index to ensure edges are rendered on top
                    });
                }
                
                showToast(`Applied ${layoutName} layout with separation ${separation}`);
                
                // Call handleFixEdgeVisibility after layout completes
                setTimeout(handleFixEdgeVisibility, 200);
            }, 300);
        });
    };

    // Add a function to automatically fix edge visibility based on zoom level
    const autoFixEdgesOnZoom = (zoomLevel) => {
        if (!cyRef.current) return;
        
        // Define zoom thresholds where edges might become problematic
        const lowZoomThreshold = 0.4;
        const highZoomThreshold = 2.0;
        
        // Get current edges
        const edges = cyRef.current.edges();
        
        // Apply different styles based on zoom level
        if (zoomLevel <= lowZoomThreshold) {
            // At low zoom levels, make edges more visible
            edges.style({
                'width': 3, // Thicker lines
                'opacity': 1,
                'z-index': 999,
                'line-color': '#aaa', // Slightly darker for better visibility
                'target-arrow-color': '#aaa',
                'arrow-scale': 2.0, // Larger arrows
                'visibility': 'visible',
                'display': 'element'
            });
        } else if (zoomLevel >= highZoomThreshold) {
            // At high zoom levels, ensure edges are still visible
            edges.style({
                'width': 1.5, // Thinner lines for detail
                'opacity': 1,
                'z-index': 999,
                'line-color': '#ccc',
                'target-arrow-color': '#ccc',
                'arrow-scale': 1.2,
                'visibility': 'visible',
                'display': 'element'
            });
        } else {
            // At normal zoom levels, use default styles
            edges.style({
                'width': 2,
                'opacity': 1,
                'z-index': 999,
                'line-color': '#ccc',
                'target-arrow-color': '#ccc',
                'arrow-scale': 1.5,
                'visibility': 'visible',
                'display': 'element'
            });
        }
        
        // Force a style update
        cyRef.current.style().update();
    };

    // Modify the zoom event handler to use the autoFixEdgesOnZoom function
    useEffect(() => {
        if (!cyRef.current) return;
        
        const cy = cyRef.current;
        
        // Function to ensure edges are visible after zoom
        const onZoom = () => {
            const currentZoom = cy.zoom();
            
            // Apply automatic edge fixing based on zoom level
            autoFixEdgesOnZoom(currentZoom);
            
            // Basic edge visibility enforcement
            cy.edges().style({
                'display': 'element',
                'visibility': 'visible',
                'opacity': 1,
                'z-index': 999
            });
            
            // Force a style update to ensure changes are applied
            cy.style().update();
        };
        
        // Track zoom level to detect significant changes
        let lastZoomLevel = cy.zoom();
        
        // Add event listeners for zoom
        cy.on('zoom', () => {
            // Get current zoom level
            const currentZoom = cy.zoom();
            
            // Check if zoom level has changed significantly
            const zoomDelta = Math.abs(currentZoom - lastZoomLevel);
            if (zoomDelta > 0.3) {
                // If significant zoom change, call the more aggressive fix
                handleFixEdgeVisibility();
                lastZoomLevel = currentZoom;
            } else {
                // For minor zoom changes, just update edge styles
                onZoom();
                lastZoomLevel = currentZoom;
            }
        });
        
        cy.on('pan', onZoom);
        
        // Also add a debounced version for better performance
        let zoomTimeout;
        cy.on('zoom', () => {
            clearTimeout(zoomTimeout);
            zoomTimeout = setTimeout(() => {
                onZoom();
            }, 100);
        });
        
        return () => {
            // Clean up event listeners
            cy.off('zoom');
            cy.off('pan', onZoom);
            clearTimeout(zoomTimeout);
        };
    }, []);

    return (
        <div className="database-graph" ref={containerRef} style={{ height: graphHeight }}>
            <div id="loading">Loading...</div>
            <div id="cy" style={{ flex: '1 1 auto', minHeight: '300px' }}></div>
            <div className="watermark">www.conncepts.pro</div>
            <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-toggle" onClick={toggleSidebar}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                    </svg>
                </div>
                <div className="version-info">ERP Version: {APP_CONFIG.version.erp}</div>
                <div className="panel">
                    <h3>Search</h3>
                    <input 
                        type="text" 
                        className="search-input" 
                        placeholder="Search nodes..."
                        onChange={handleSearch}
                    />
                    <div id="search-results"></div>
                </div>
                <div className="panel">
                    <h3>View Controls</h3>
                    <div className="control-group">
                        <input 
                            type="number" 
                            min="1" 
                            max="5" 
                            defaultValue="1"
                            onChange={handleDepthChange}
                        />
                        <label>Node Depth (1-5)</label>
                    </div>
                    <div className="control-group">
                        <input 
                            type="checkbox"
                            name="hideUnselected"
                            onChange={handleHideUnselected}
                        />
                        <label>Hide Unselected Nodes</label>
                    </div>
                    <div className="control-group">
                        <input 
                            type="checkbox"
                            name="darkMode"
                            checked={isDarkMode}
                            onChange={handleDarkModeToggle}
                        />
                        <label>Dark Mode</label>
                    </div>
                    <div className="control-group">
                        <input 
                            type="range"
                            min="30"
                            max="200"
                            step="10"
                            value={nodeSeparation}
                            onChange={handleNodeSeparationChange}
                            className="separation-slider"
                        />
                        <label>Node Separation: {nodeSeparation}</label>
                    </div>
                    <div className="control-group">
                        <select 
                            value={currentLayout}
                            onChange={(e) => applyLayout(e.target.value)}
                            className="layout-selector"
                        >
                            <option value="fcose">F-Cose (Default)</option>
                            <option value="cola">Cola (Force-Directed)</option>
                            <option value="cise">CiSE (Circular)</option>
                            <option value="dagre">Dagre (Hierarchical)</option>
                            <option value="elk">ELK (Layered)</option>
                            <option value="klay">Klay (Layered)</option>
                            <option value="cose-bilkent">Cose-Bilkent</option>
                            <option value="cose">Cose (Classic)</option>
                            <option value="euler">Euler (Physics)</option>
                        </select>
                        <label>Layout</label>
                    </div>
                    <button onClick={handleClearSelection}>Clear Selection</button>
                    <button onClick={handleCenterView}>Center View</button>
                    <button onClick={handleShowHeavyNodes}>
                        Show Heavy Nodes ({'>'}6 connections)
                    </button>
                    <button onClick={handleFixEdgeVisibility}>
                        Fix Edge Visibility
                    </button>
                </div>
                <div className="panel">
                    <h3>Selected Node</h3>
                    <div id="selected-name">None</div>
                    <div id="selected-connections">0</div>
                    <div id="edge-info" style={{display: 'none'}}></div>
                    
                    {/* Add path information */}
                    {paths.length > 0 && (
                        <div className="paths-info">
                            <h4>Paths Between Selected Nodes</h4>
                            {paths.map((path, index) => {
                                const isOnlyPath = paths.length === 1;
                                const pathKey = `path-${index}`;
                                return (
                                    <div key={pathKey} className="path-group">
                                        <div 
                                            className="path-header"
                                            onClick={() => toggleRelationshipGroup(pathKey)}
                                            style={{ cursor: 'pointer', padding: '8px', backgroundColor: '#f8f9fa' }}
                                        >
                                            <span>Path {path.pathIndex} - Length: {path.length} nodes</span>
                                            <span style={{ marginLeft: '8px' }}>
                                                {expandedGroups[pathKey] ? '▼' : '▶'}
                                            </span>
                                        </div>
                                        <div className={`path-content ${expandedGroups[pathKey] || isOnlyPath ? 'expanded' : ''}`}>
                                            <div className="path-nodes">
                                                {path.nodes.map((node, nodeIndex) => {
                                                    const nodeKey = `${pathKey}-node-${nodeIndex}`;
                                                    return (
                                                        <div key={nodeKey} className="path-node">
                                                            <div 
                                                                className="node-header"
                                                                onClick={() => toggleRelationshipGroup(nodeKey)}
                                                                style={{ cursor: 'pointer', padding: '4px 8px', backgroundColor: '#e9ecef' }}
                                                            >
                                                                <span>{node}</span>
                                                                <span style={{ marginLeft: '8px' }}>
                                                                    {expandedGroups[nodeKey] ? '▼' : '▶'}
                                                                </span>
                                                            </div>
                                                            <div className={`node-relationships ${expandedGroups[nodeKey] ? 'expanded' : ''}`}>
                                                                {nodeIndex < path.relationships.length && (
                                                                    <div className="relationship-details">
                                                                        {path.relationships[nodeIndex].fieldRelationships.map((field, fieldIndex) => (
                                                                            <div key={`${nodeKey}-field-${fieldIndex}`} className="field-relationship">
                                                                                {field.sourceField} → {field.targetField}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    
                    {relationships.length > 0 && (
                        <div className="relationship-tree">
                            <h4 style={{ padding: '8px 10px', margin: 0, borderBottom: '1px solid #dee2e6' }}>
                                Relationships
                            </h4>
                            {relationships.map((nodeGroup) => (
                                <div key={`node-${nodeGroup.nodeId}`} className="relationship-group">
                                    {/* Selected node header */}
                                    <div 
                                        className="relationship-header"
                                        onClick={() => toggleRelationshipGroup(`node-${nodeGroup.nodeId}`)}
                                        style={{ backgroundColor: '#d1e7ff' }}
                                    >
                                        <span>{nodeGroup.label}</span>
                                        <span>{expandedGroups[`node-${nodeGroup.nodeId}`] ? '▼' : '▶'}</span>
                                    </div>
                                    
                                    {/* Selected node content - depth levels */}
                                    <div className={`relationship-content ${
                                        expandedGroups[`node-${nodeGroup.nodeId}`] || 
                                        (nodeGroup.connectedNodeGroups && nodeGroup.connectedNodeGroups.length === 1) ? 
                                        'expanded' : ''
                                    }`}>
                                        {/* Connected Node Groups */}
                                        {nodeGroup.connectedNodeGroups && nodeGroup.connectedNodeGroups.map((group) => (
                                            <div key={`group-${group.nodeId}`} className="relationship-group" style={{ marginLeft: '10px' }}>
                                                {/* Connected node group header */}
                                                <div 
                                                    className="relationship-header"
                                                    onClick={() => toggleRelationshipGroup(`group-${group.nodeId}`)}
                                                    style={{ backgroundColor: '#e9ecef' }}
                                                >
                                                    <span>
                                                        <span className="depth-badge">L{group.depth}</span>
                                                        {group.label} <span className="relationship-count">({group.relationships.length})</span>
                                                    </span>
                                                    <span>{expandedGroups[`group-${group.nodeId}`] ? '▼' : '▶'}</span>
                                                </div>
                                                
                                                {/* Connected node relationships */}
                                                <div className={`relationship-content ${
                                                    expandedGroups[`group-${group.nodeId}`] || group.relationships.length === 1 ? 'expanded' : ''
                                                }`}>
                                                    {group.relationships.map((rel, idx) => (
                                                        <div key={`rel-${rel.source.id()}-${rel.target.id()}-${idx}`} className="field-relationship-group">
                                                                    <div className="relationship-item">
                                                                        {rel.source.id() === nodeGroup.nodeId ? (
                                                                            <>
                                                                                <span>{rel.target.data('label')}</span>
                                                                                <span className="relationship-arrow">←</span>
                                                                                <span className="selected-node-ref">{rel.source.data('label')}</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <span>{rel.source.data('label')}</span>
                                                                                <span className="relationship-arrow">→</span>
                                                                                <span className="selected-node-ref">{rel.target.data('label')}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                            {rel.fieldRelationships && rel.fieldRelationships.length > 0 && (
                                                                    <ul className="field-relationships">
                                                                    {rel.fieldRelationships.map((fieldRel, fieldIdx) => (
                                                                        <li key={fieldIdx}>
                                                                                <span className="source-field">{fieldRel.sourceField}</span> → 
                                                                                <span className="target-field">{fieldRel.targetField}</span>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                        )}
                                                                    </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div id="toast" className="toast"></div>
        </div>
    );
};

export default DatabaseGraph; 